import { buildPageHead } from "@lib/meta"
import * as m from "@paraglide/messages"
import type { SupportedLocale } from "@site-types/content"

/**
 * Head builder for the PLAYDATA microsite.
 *
 * `localizedRouteHead` cannot be reused: its `pageLabels` lookup falls back to
 * the PLAYBRAIN home labels for any page it does not know, which would give
 * every microsite page PLAYBRAIN's title and description. These pages resolve
 * their own title and description and pass them through explicitly.
 */
export type PlaydataHeadOptions = {
  locale: SupportedLocale
  /** Path after the locale segment, e.g. "/playdata/pricing". */
  path: string
  title: string
  description: string
  type?: "article" | "website"
  image?: string
  robots?: string
}

/**
 * Social-share cards, straight off the live site — one per locale, because
 * PLAYDATA ships a Japanese and an English card and PLAYBRAIN's default
 * carries the wrong brand entirely.
 */
const playdataOgImages: Record<SupportedLocale, string> = {
  en: "/images/playdata/og-playdata-en.png",
  ja: "/images/playdata/og-playdata-ja.png",
}

export function playdataRouteHead({
  locale,
  path,
  title,
  description,
  type,
  image,
  robots,
}: PlaydataHeadOptions) {
  const siteName = m.playdata_meta_siteName({}, { locale })
  // Live titles all end in the site name, so the suffix belongs here rather
  // than in each route — and in each of the two message files.
  const fullTitle = playdataTitle(locale, title)
  return buildPageHead({
    locale,
    path: `/${locale}${path}`,
    title: fullTitle,
    description,
    ogTitle: fullTitle,
    ogDescription: description,
    image: image ?? playdataOgImages[locale],
    type: type ?? "website",
    author: siteName,
    publisher: m.playdata_meta_publisher({}, { locale }),
    siteName,
    twitterCard: "summary_large_image",
    twitterTitle: fullTitle,
    twitterDescription: description,
    robots,
  })
}

/** Title in the microsite's `<page> — PLAYDATA` form. */
export function playdataTitle(locale: SupportedLocale, pageTitle: string) {
  return `${pageTitle} — ${m.playdata_meta_siteName({}, { locale })}`
}
